import React, { useContext, useCallback, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress, Grid } from '@material-ui/core';
import clsx from 'clsx';
import { Monitor } from '@moderepo/mode-apis';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { ITEMS_PER_PAGE_20 } from '@moderepo/mode-app-components';
import { modeUIContext, selectSlideOutPanelOptions, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCMonitorsContainer, MonitorsTableHiddenFields } from './RCMonitorsContainer';
import { isMonitorPreviewCompOptions, RCMonitorPreviewCompOptions, RCSlideOutPanelCompType } from './RCProjectConfig';


/**
 * Define the list of fields we want to hide in the MonitorsTable. This will depends on if there is an Monitor selected. If there is an Monitor
 * selected and are showing Monitor details on the Right side, we can hide some of the unnecessary columns.
 * This will create 2 set of IssueTableHiddenFields. The first set will show more column which should be used when there is no issue selected.
 * The second set has more columns hidden which is to be used when an issue is selected
 */
const monitorsTableHiddenFields: MonitorsTableHiddenFields[] = [{
    conditions    : true,
    actions       : true,
    createdAgentId: true,
    updatedAgentId: true,
    updatedAt     : true,
}, {
    targetHomeId  : true,
    conditions    : true,
    actions       : true,
    createdAgentId: true,
    updatedAgentId: true,
    updatedAt     : true,
}];


/**
 * This page Display ALL the Project's Monitor
 */
export const RCMonitorsPage = () => {
    // Get the params from the route or url
    const params = useParams();
    const projectId = Number(params.projectId);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, projectId);
    const [selectedMonitorId, setSelectedMonitorId] = useState<number | undefined>();
    const { trans } = useRitzTranslation(project);
    const slideOutPanelOptions = selectSlideOutPanelOptions(uiState);


    // This useEffect is used for checking the slideOutPanelOptions to see if it is for showing a monitor.
    // if it is, automatically set it as the selectedMonitorId
    useEffect(() => {
        if (isMonitorPreviewCompOptions(slideOutPanelOptions) && slideOutPanelOptions.compProps.data.monitorId !== undefined) {
            setSelectedMonitorId(slideOutPanelOptions.compProps.data.monitorId);
        } else {
            setSelectedMonitorId(undefined);
        }
    }, [slideOutPanelOptions]);


    /**
     * Show the slide out panel for a monitor preview panel for the specified monitorId. If monitorId is undefined, it
     * means to show the panel for creating a new monitor
     */
    const setSelectedMonitor = useCallback((monitorId: number | undefined) => {
        setSelectedMonitorId(monitorId);

        const options: RCMonitorPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.MONITOR_PREVIEW,
            compProps: {
                data: {
                    projectId,
                    monitorId,
                },
            },
        };
        uiDispatch(showSlideOutPanel(options));

    }, [projectId, uiDispatch]);



    /**
     * Handler for Create Alert button click either from Fab menu or from AlertTableComp
     */
    const onCreateAlertClicked = useCallback(() => {
        setSelectedMonitor(undefined);
    }, [setSelectedMonitor]);



    return (
        <>
            <div className={clsx('page-main-content')}>
                <div className="title-bar">
                    <div className="title-bar-item title-text">
                        <div className="page-title">
                            {trans('page.title.alerts')}
                        </div>
                        <div className="page-subtitle">
                            {trans('page.subtitle.alerts')}
                        </div>
                    </div>
                </div>

                {project && (
                    <Grid container spacing={3}>
                        <Grid className="alerts-container-grid" item xs={12}>
                            <RCMonitorsContainer
                                projectId={projectId}
                                itemsPerPage={ITEMS_PER_PAGE_20}
                                onAlertSelected={(monitor: Monitor) => {
                                    setSelectedMonitor(monitor.id);
                                }}
                                selectedMonitorId={selectedMonitorId}
                                canRemoveAlerts={false}
                                canCreateAlerts
                                onCreateAlertClicked={onCreateAlertClicked}
                                hiddenFields={monitorsTableHiddenFields[isMonitorPreviewCompOptions(slideOutPanelOptions) ? '1' : 0]}
                            />
                        </Grid>
                    </Grid>
                )}

                {!project && (
                    <CircularProgress />
                )}

            </div>
        </>
    );
};
